const PREDEFINED_ROLE_COLORS = {
  "super admin": {
    primary: "#c41d7f",
    light: "#eb2f96",
    lighter: "#ff85c0",
    gradient: "linear-gradient(90deg, #eb2f96 0%, #c41d7f 100%)",
    shadow: "rgba(196, 29, 127, 0.15)",
  },
  admin: {
    primary: "#cf1322",
    light: "#ff4d4f",
    lighter: "#ff7875",
    gradient: "linear-gradient(90deg, #ff4d4f 0%, #cf1322 100%)",
    shadow: "rgba(207, 19, 34, 0.15)",
  },
  manager: {
    primary: "#0050b3",
    light: "#1890ff",
    lighter: "#40a9ff",
    gradient: "linear-gradient(90deg, #1890ff 0%, #0050b3 100%)",
    shadow: "rgba(0, 80, 179, 0.15)",
  },
  "sales agent": {
    primary: "#389e0d",
    light: "#52c41a",
    lighter: "#73d13d",
    gradient: "linear-gradient(90deg, #52c41a 0%, #389e0d 100%)",
    shadow: "rgba(56, 158, 13, 0.15)",
  },
};

const DYNAMIC_COLOR_PALETTE = [
  {
    primary: "#722ed1",
    light: "#9254de",
    lighter: "#b37feb",
    gradient: "linear-gradient(90deg, #9254de 0%, #722ed1 100%)",
    shadow: "rgba(114, 46, 209, 0.15)",
  },
  {
    primary: "#d4380d",
    light: "#fa541c",
    lighter: "#ff7a45",
    gradient: "linear-gradient(90deg, #fa541c 0%, #d4380d 100%)",
    shadow: "rgba(212, 56, 13, 0.15)",
  },
  {
    primary: "#006d75",
    light: "#13c2c2",
    lighter: "#36cfc9",
    gradient: "linear-gradient(90deg, #13c2c2 0%, #006d75 100%)",
    shadow: "rgba(0, 109, 117, 0.15)",
  },
  {
    primary: "#ad6800",
    light: "#faad14",
    lighter: "#ffc53d",
    gradient: "linear-gradient(90deg, #faad14 0%, #ad6800 100%)",
    shadow: "rgba(173, 104, 0, 0.15)",
  },
  {
    primary: "#08979c",
    light: "#36cfc9",
    lighter: "#5cdbd3",
    gradient: "linear-gradient(90deg, #36cfc9 0%, #08979c 100%)",
    shadow: "rgba(8, 151, 156, 0.15)",
  },
  {
    primary: "#d4b106",
    light: "#fadb14",
    lighter: "#ffec3d",
    gradient: "linear-gradient(90deg, #fadb14 0%, #d4b106 100%)",
    shadow: "rgba(212, 177, 6, 0.15)",
  },
  {
    primary: "#9e1068",
    light: "#eb2f96",
    lighter: "#f759ab",
    gradient: "linear-gradient(90deg, #eb2f96 0%, #9e1068 100%)",
    shadow: "rgba(158, 16, 104, 0.15)",
  },
  {
    primary: "#10239e",
    light: "#2f54eb",
    lighter: "#597ef7",
    gradient: "linear-gradient(90deg, #2f54eb 0%, #10239e 100%)",
    shadow: "rgba(16, 35, 158, 0.15)",
  },
  {
    primary: "#7cb305",
    light: "#a0d911",
    lighter: "#bae637",
    gradient: "linear-gradient(90deg, #a0d911 0%, #7cb305 100%)",
    shadow: "rgba(124, 179, 5, 0.15)",
  },
  {
    primary: "#ad4e00",
    light: "#fa8c16",
    lighter: "#ffa940",
    gradient: "linear-gradient(90deg, #fa8c16 0%, #ad4e00 100%)",
    shadow: "rgba(173, 78, 0, 0.15)",
  },
];

/**
 * Cache for dynamic role color assignments
 * Ensures consistent colors for the same role across page loads
 */
const roleColorCache = new Map();

/**
 * Gets a stable hash code from a string (for consistent color assignment)
 * @param {string} str - Input string
 * @returns {number} Hash code
 */
function getHashCode(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  return Math.abs(hash);
}

/**
 * Gets color configuration for a role
 * @param {string} roleName - Name of the role
 * @returns {Object} Color configuration object
 */
export function getRoleColors(roleName) {
  if (!roleName) {
    return DYNAMIC_COLOR_PALETTE[0];
  }

  // Check if it's a predefined role
  if (PREDEFINED_ROLE_COLORS[roleName]) {
    return PREDEFINED_ROLE_COLORS[roleName];
  }

  // Check cache first
  if (roleColorCache.has(roleName)) {
    return roleColorCache.get(roleName);
  }

  // Assign a color from the dynamic palette based on hash
  const hash = getHashCode(roleName);
  const colorIndex = hash % DYNAMIC_COLOR_PALETTE.length;
  const color = DYNAMIC_COLOR_PALETTE[colorIndex];

  roleColorCache.set(roleName, color);

  return color;
}

/**
 * Gets the CSS class name for a role (kebab-case)
 * @param {string} roleName - Name of the role
 * @returns {string} CSS class name
 */
export function getRoleClassName(roleName) {
  if (!roleName) return "default";

  return roleName
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");
}

/**
 * Gets primary color for a role
 * @param {string} roleName - Name of the role
 * @returns {string} Primary color hex
 */
export function getRolePrimaryColor(roleName) {
  return getRoleColors(roleName).primary;
}

/**
 * Gets light color for a role
 * @param {string} roleName - Name of the role
 * @returns {string} Light color hex
 */
export function getRoleLightColor(roleName) {
  return getRoleColors(roleName).light;
}

/**
 * Gets gradient for a role
 * @param {string} roleName - Name of the role
 * @returns {string} CSS gradient string
 */
export function getRoleGradient(roleName) {
  return getRoleColors(roleName).gradient;
}

/**
 * Gets shadow color for a role
 * @param {string} roleName - Name of the role
 * @returns {string} RGBA shadow string
 */
export function getRoleShadow(roleName) {
  return getRoleColors(roleName).shadow;
}

/**
 * Generates inline styles for a role stat card
 * @param {string} roleName - Name of the role
 * @returns {Object} React inline styles object
 */
export function getRoleStatCardStyles(roleName) {
  const colors = getRoleColors(roleName);

  return {
    "--role-primary": colors.primary,
    "--role-light": colors.light,
    "--role-gradient": colors.gradient,
    "--role-shadow": colors.shadow,
  };
}

export function clearRoleColorCache() {
  roleColorCache.clear();
}

export default {
  getRoleColors,
  getRoleClassName,
  getRolePrimaryColor,
  getRoleLightColor,
  getRoleGradient,
  getRoleShadow,
  getRoleStatCardStyles,
  clearRoleColorCache,
};
